import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import {
  openBasketOverviewStyle
} from 'components/ordering/basket-support/open-basket-overview/open-basket-overview.style.js'
import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { t, TranslateResult } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  OPEN_BASKET_OVERVIEW_API, OpenBasketOverviewAPI,
} from 'components/ordering/basket-support/open-basket-overview/open-basket-overview-api.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import {
  GetBasketsByParty,
  NoBasketSelectedDTO,
  NoNameGivenDTO,
  PartyBasketDTO, PartyBasketResponse,
  UserInfoDTO
} from '@cloudsuite/cs-ordering-defs'
import { calculateRelativeDate } from 'components/shared/datetime.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'


const translations: { [key: string]: () => TranslateResult } = {
  user: () => t('csc_open_basket_overview_header_user', {
    ns: 'csc-open-basket-overview',
    default: 'User',
  }),
  last_modified: () => t('csc_open_basket_overview_header_last_modified', {
    ns: 'csc-open-basket-overview',
    default: 'Last modified',
  }),
  number_of_lines: () => t('csc_open_basket_overview_header_number_of_lines', {
    ns: 'csc-open-basket-overview',
    default: 'Number of lines',
  }),
  basket_name: () => t('csc_open_basket_overview_header_basket_name', {
    ns: 'csc-open-basket-overview',
    default: 'Basket name',
  }),
}

const parseUserName = (user: UserInfoDTO) => {
  let full_name = `${user.first_name} ${user.middle_name} ${user.last_name}`
  if (user.middle_name === '') {
    full_name = `${user.first_name} ${user.last_name}`
  }
  return full_name
}

const badgeOrNotRenderer = (basket_id: string, currentBasket: string | NoBasketSelectedDTO) => {
  if (basket_id === currentBasket) {
    return html`<cse-badge variant="info">${t('currently_editing', { ns: 'csc-open-basket-overview', default: 'Currently editing' })}</cse-badge>`
  }
  return nothing
}

type BadgeAndUser = {
  basket_id: string,
  current_basket: string | NoBasketSelectedDTO,
  user: UserInfoDTO,
}

interface InternalPartyBasketDTO extends PartyBasketDTO {
  badge_and_user: BadgeAndUser,
  action: string,
}

@customElement('csc-open-basket-overview')
export class CSCOpenBasketOverview extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, openBasketOverviewStyle]

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @state()
  private onSmallDevice: boolean = true

  @state()
  private openBaskets: InternalPartyBasketDTO[] = []

  @state()
  private currentBasket: string | NoBasketSelectedDTO = new NoBasketSelectedDTO()

  @state()
  private errorOccurred: boolean = false

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @inject(OPEN_BASKET_OVERVIEW_API)
  private openBasketOverviewAPI!: OpenBasketOverviewAPI

  @property({ attribute: 'shop-id', type: Number })
  private shopId!: number

  @property({ attribute: 'party-id', type: Number })
  private partyId!: number

  @property({ attribute: 'basket-index-url' })
  private basketIndexUrl = ''

  private _locale: string = 'en-US'

  @property({ attribute: 'locale' })
  set locale(value: string) {
    this._locale = value.replace('_', '-')
  }
  get locale(){ return this._locale }

  override async connectedCallback() {
    super.connectedCallback()

    createEffect(() => {
      this.onSmallDevice = this.breakpoints.small()
    })

    this.breakpoints.matchMedia(breakpoints.medium)

    let response: PartyBasketResponse | null = null
    try {
      response = (await this.openBasketOverviewAPI.getOpenBasketsForParty(new GetBasketsByParty(this.partyId, this.shopId)))
      this.currentBasket = response.current_basket

      this.openBaskets = response.baskets
        .sort((a, b) => (a.last_activity_date.getTime() - b.last_activity_date.getTime()) * -1)
        .map((basket) => ({
          ...basket,
          badge_and_user: {
            basket_id: basket.basket_id,
            current_basket: this.currentBasket,
            user: basket.user,
          },
          action: basket.basket_id,
        } as InternalPartyBasketDTO))

    } catch (error: any) {
      error
      this.errorOccurred = true
    }
  }

  private async viewBasket(basket_id: string): Promise<void> {
    await this.openBasketOverviewAPI.viewBasket(basket_id)
    this.windowLocation.redirect(new URL(this.basketIndexUrl, window.location.href))
  }

  private _parseBasketName(name: NoNameGivenDTO | string): string {
    if (typeof name === 'string') {
      return name
    }
    return ''
  }

  private _badgeAndUserRenderer(badge_and_user: BadgeAndUser) {
    return html`
      ${badgeOrNotRenderer(badge_and_user.basket_id, badge_and_user.current_basket)}
      
      <cse-text as="span" variant="body-s">${parseUserName(badge_and_user.user)}</cse-text>
      <cse-text as="span" variant="caption-m">
        <span class="no-text-wrap">
          ${badge_and_user.user.email_address}
        </span>
      </cse-text>
    `
  }

  private _lastActivityDateRenderer = (last_activity_date: Date) => {
    if (last_activity_date === undefined) {
      return nothing
    }

    return html`
      <cse-text as="span" variant="body-s">${calculateRelativeDate(last_activity_date, this.locale)}</cse-text>

      <cse-text as="span" variant="caption-m">
        <span class="no-text-wrap">
          ${new Intl.DateTimeFormat(this.locale, {
            dateStyle: 'full',
            timeStyle: 'short',
          }).format(last_activity_date)}
        </span>
      </cse-text>
    `
  }

  private _actionCellRenderer = (basket_id: string) => {
    return html`
      <cse-icon-button variant="secondary" slot="actions" @click="${async () => await this.viewBasket(basket_id)}">
        <cse-icon icon-name="angle-right" icon-style="light"></cse-icon>
      </cse-icon-button>
    `
  }

  private _columns = [
    { name: translations['user']?.(), key: 'badge_and_user', width: 'auto', cellRenderer: this._badgeAndUserRenderer },
    { name: translations['last_modified']?.(), key: 'last_activity_date', width: 'auto', cellRenderer: this._lastActivityDateRenderer },
    { name: translations['number_of_lines']?.(), key: 'line_count', width: 'auto' },
    { name: translations['basket_name']?.(), key: 'name', width: 'auto', cellRenderer: this._parseBasketName },
    { name: '', key: 'action', width: 'auto', align: 'right', cellRenderer: this._actionCellRenderer },
  ]

  private listRenderer = () => {
    return html`
      <cse-list>
        ${this.openBaskets.map((basket) => {
          return html`
            <cse-list-item>
              <cse-selectable>
                <div class="item-heading">
                  ${this._badgeAndUserRenderer(basket.badge_and_user)}
                </div>
                
                <div class="item-content">
                  <div class="columns">
                    <cse-text as="span" variant="caption-s">
                      ${translations['last_modified']?.()}
                    </cse-text>

                    <div class="last-activity-date">
                      ${this._lastActivityDateRenderer(basket.last_activity_date)}
                    </div>

                    <cse-text as="span" variant="caption-s">
                      ${translations['number_of_lines']?.()}
                    </cse-text>

                    <cse-text as="span" variant="body-s">
                      ${basket.line_count}
                    </cse-text>

                    <cse-text as="span" variant="caption-s">
                      ${translations['basket_name']?.()}
                    </cse-text>

                    <cse-text as="span" variant="body-s">
                      ${this._parseBasketName(basket.name)}
                    </cse-text>
                  </div>

                  ${this._actionCellRenderer(basket.action)}
                </div>
              </cse-selectable>
            </cse-list-item>
          `
        })}
      </cse-list>
    `
  }

  private tableRenderer = () => {
    return html`
      <csc-table variant="secondary" .columns="${this._columns}" .data="${this.openBaskets}"></csc-table>
    `
  }

  renderErrorMessage() {
    return html`
      <cse-alert variant="error">
        ${t('error_message_title', {
          ns: 'csc-open-basket-overview',
          default: 'Permission Denied'
        })}
        <div slot="description">
          ${t('error_message_description', {
            ns: 'csc-open-basket-overview',
            default: 'Permission Denied. You do not have permission to access this site.'
          })}
        </div>
        <cse-link slot="link" url="${t('error_message_contact_url', {
          ns: 'csc-open-basket-overview',
          default: '/contact'
        })}">
          ${t('error_message_contact_text', {
            ns: 'csc-open-basket-overview',
            default: 'Contact'
          })}
        </cse-link>
      </cse-alert>
    `
  }

  override render = () => html`
    ${this.errorOccurred ? this.renderErrorMessage() : this.onSmallDevice ?
      this.listRenderer() :
      this.tableRenderer()
    }
  `
}
